/**
 * Send Gift Flow - Category Selection
 * ====================================
 * Step 1: Choose a gift category (or view all categories)
 * Beautiful luxury category display with tier-based limits
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Crown, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SendGiftSelectPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDark, setIsDark] = useState(true);
  const [categories, setCategories] = useState([]);
  const [membershipLimits, setMembershipLimits] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if coming with pre-selected category or recipient
  const preSelectedCategory = location.state?.giftCategory;
  const preSelectedRecipient = location.state?.preSelectedRecipient;

  useEffect(() => {
    const checkTheme = () => {
      const saved = localStorage.getItem('nearU_theme');
      setIsDark(saved !== 'light');
    };
    checkTheme();
    const interval = setInterval(checkTheme, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // If pre-selected category, navigate directly to recipient selection
    if (preSelectedCategory && categories.length > 0) {
      const cat = categories.find(c => c.id === preSelectedCategory);
      if (cat) {
        handleCategorySelect(cat);
      }
    }
  }, [preSelectedCategory, categories]);

  const fetchData = async () => {
    try {
      const [catRes, limitsRes] = await Promise.all([
        fetch(`${API}/card/categories`),
        fetch(`${API}/card/membership-limits`)
      ]);

      if (catRes.ok) setCategories(await catRes.json());
      if (limitsRes.ok) setMembershipLimits(await limitsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const colors = {
    background: isDark ? '#0D0D0D' : '#FDFCFA',
    cardBg: isDark ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)',
    text: isDark ? '#E6D7C3' : '#1A1410',
    textMuted: isDark ? '#8A7355' : '#4A4035',
    border: isDark ? 'rgba(165, 124, 51, 0.2)' : 'rgba(165, 124, 51, 0.15)',
    accent: '#D4AF37',
    gold: 'linear-gradient(135deg, #D7B56D 0%, #A57C33 100%)'
  };

  const handleCategorySelect = (category) => {
    // If we have a pre-selected recipient, skip to amount selection
    if (preSelectedRecipient) {
      navigate('/send-gift/amount', {
        state: {
          category: {
            id: category.id,
            name: category.name,
            emoji: category.emoji,
            image: category.image,
            description: category.description,
            suggestedAmounts: category.suggested_amounts,
            minAmount: category.min_amount_cents,
            maxAmount: category.max_amount_cents,
            premium: category.premium
          },
          recipient: preSelectedRecipient
        }
      });
    } else {
      // Normal flow - go to recipient selection
      navigate('/send-gift/recipient', {
        state: {
          category: {
            id: category.id,
            name: category.name,
            emoji: category.emoji,
            image: category.image,
            description: category.description,
            suggestedAmounts: category.suggested_amounts,
            minAmount: category.min_amount_cents,
            maxAmount: category.max_amount_cents,
            premium: category.premium
          }
        }
      });
    }
  };

  const getTierGradient = (tier) => {
    switch(tier) {
      case 'diamond': return 'linear-gradient(135deg, #E8F4F8 0%, #B9F2FF 30%, #89CFF0 70%, #00BFFF 100%)';
      case 'gold': return 'linear-gradient(135deg, #FFF8E1 0%, #FFD700 30%, #DAA520 70%, #B8860B 100%)';
      default: return 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 30%, #BDBDBD 70%, #9E9E9E 100%)';
    }
  };

  const getTierTextColor = (tier) => {
    return tier === 'gold' ? '#5D4E37' : tier === 'diamond' ? '#1A5276' : '#424242';
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      paddingBottom: '100px', 
      background: isDark 
        ? 'linear-gradient(180deg, #0D0D0D 0%, #1A0F0A 50%, #0D0D0D 100%)'
        : 'linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)'
    }}>
      {/* Header */}
      <div style={{ 
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate('/gifts')}
          style={{
            background: 'rgba(138, 115, 85, 0.15)',
            border: 'none',
            borderRadius: '10px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: colors.text
          }}
        >
          <ChevronLeft size={22} />
        </button>
        
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontFamily: 'Cinzel, serif', 
            fontSize: '22px',
            fontWeight: '600',
            color: colors.text,
            margin: 0
          }}>
            Send a Gift
          </h1>
          <p style={{ 
            color: colors.textMuted, 
            fontSize: '13px', 
            margin: '4px 0 0 0' 
          }}>
            Step 1 of 5 • Choose an experience
          </p>
        </div>

        {/* Tier Badge */}
        {membershipLimits && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            padding: '5px 12px',
            background: getTierGradient(membershipLimits.tier),
            borderRadius: '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Crown size={11} color={getTierTextColor(membershipLimits.tier)} />
            <span style={{ 
              fontSize: '10px', 
              fontWeight: '700',
              color: getTierTextColor(membershipLimits.tier),
              textTransform: 'uppercase'
            }}>
              {membershipLimits.tier_name}
            </span>
          </div>
        )}
      </div>

      {/* Step Progress */}
      <div style={{ padding: '0 20px 24px' }}>
        <div style={{ 
          display: 'flex', 
          gap: '6px',
          marginBottom: '8px'
        }}>
          {[1, 2, 3, 4, 5].map((step) => (
            <div
              key={step}
              style={{
                flex: 1,
                height: '3px',
                borderRadius: '2px',
                background: step === 1 ? colors.gold : 'rgba(138, 115, 85, 0.2)'
              }}
            />
          ))}
        </div>
      </div>

      {/* Title Section */}
      <div style={{ padding: '0 20px 20px', textAlign: 'center' }}>
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '26px',
          color: colors.text,
          margin: '0 0 8px 0'
        }}>
          What would you like to send?
        </h2>
        <p style={{
          color: colors.textMuted,
          fontSize: '14px',
          margin: 0
        }}>
          Choose an unforgettable experience
        </p>
      </div>

      {/* Categories */}
      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: colors.textMuted }}>
          Loading experiences...
        </div>
      ) : (
        <div style={{ padding: '0 16px' }}>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategorySelect(cat)}
              data-testid={`category-${cat.id}`}
              style={{
                width: '100%',
                position: 'relative',
                height: '120px',
                borderRadius: '18px',
                border: cat.premium ? '2px solid rgba(215, 181, 109, 0.5)' : `1px solid ${colors.border}`,
                background: colors.cardBg,
                cursor: 'pointer',
                overflow: 'hidden',
                textAlign: 'left',
                marginBottom: '12px',
                padding: 0
              }}
            >
              {/* Background Image */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${cat.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.4
              }} />
              
              {/* Gradient Overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: isDark 
                  ? 'linear-gradient(90deg, rgba(26, 15, 10, 0.95) 0%, rgba(26, 15, 10, 0.7) 50%, rgba(26, 15, 10, 0.4) 100%)'
                  : 'linear-gradient(90deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.7) 50%, rgba(255, 255, 255, 0.3) 100%)'
              }} />

              {/* Content */}
              <div style={{
                position: 'relative',
                height: '100%',
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}>
                {/* Top Row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '26px' }}>{cat.emoji}</span>
                      <span style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: colors.text,
                        fontFamily: 'Cinzel, serif'
                      }}>
                        {cat.name}
                      </span>
                    </div>
                    <p style={{
                      color: colors.textMuted,
                      fontSize: '13px',
                      margin: 0
                    }}>
                      {cat.description}
                    </p>
                  </div>
                  
                  {cat.premium && (
                    <div style={{
                      padding: '4px 10px',
                      background: colors.gold,
                      borderRadius: '10px',
                      fontSize: '10px',
                      fontWeight: '700',
                      color: '#2C1B13',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <Sparkles size={10} />
                      VIP
                    </div>
                  )}
                </div>

                {/* Bottom Row - Suggested Amounts */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexWrap: 'wrap'
                  }}>
                    {cat.suggested_amounts?.slice(0, 4).map((amount, idx) => (
                      <span
                        key={idx}
                        style={{
                          padding: '4px 10px',
                          background: isDark ? 'rgba(215, 181, 109, 0.12)' : 'rgba(215, 181, 109, 0.18)',
                          border: '1px solid rgba(215, 181, 109, 0.25)',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: colors.accent
                        }}
                      >
                        ${amount.toLocaleString()}
                      </span>
                    ))}
                  </div>
                  <ChevronRight size={20} color={colors.textMuted} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Tier Limits Info */}
      {membershipLimits && (
        <div style={{ 
          padding: '24px 16px 0',
          textAlign: 'center'
        }}>
          <p style={{ 
            color: colors.textMuted, 
            fontSize: '12px',
            margin: 0
          }}>
            Your {membershipLimits.tier_name} limit: Up to {membershipLimits.max_display} per gift
          </p>
        </div>
      )}
    </div>
  );
};

export default SendGiftSelectPage;
